'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface ROIInputs {
  initialInvestment: number;
  monthlyOperatingCost: number;
  implementationTimeMonths: number;
  timeSavedHoursPerWeek: number;
  averageEngineerHourlyCost: number;
  revenueIncreaseMonthly: number;
  costSavingsMonthly: number;
  successProbability: number;
  discountRate: number;
}

export default function EngineeringROICalculatorPage() {
  const [inputs, setInputs] = useState<ROIInputs>({
    initialInvestment: 50000,
    monthlyOperatingCost: 5000,
    implementationTimeMonths: 3,
    timeSavedHoursPerWeek: 20,
    averageEngineerHourlyCost: 75,
    revenueIncreaseMonthly: 0,
    costSavingsMonthly: 2000,
    successProbability: 80,
    discountRate: 10,
  });
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'engineering-roi-calculator',
    toolCategory: 'calculator',
  });

  const updateInput = <K extends keyof ROIInputs>(key: K, value: ROIInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const result = useMemo(() => {
    const totalInvestment = inputs.initialInvestment + (inputs.monthlyOperatingCost * inputs.implementationTimeMonths);
    const timeSavingsMonthly = inputs.timeSavedHoursPerWeek * 4.33 * inputs.averageEngineerHourlyCost;
    const monthlyBenefit = timeSavingsMonthly + inputs.revenueIncreaseMonthly + inputs.costSavingsMonthly;
    const annualBenefit = monthlyBenefit * 12;
    const netBenefit = (annualBenefit * 3) - totalInvestment - (inputs.monthlyOperatingCost * 36);
    const roi = totalInvestment > 0 ? (netBenefit / totalInvestment) * 100 : 0;
    const netMonthlyBenefit = monthlyBenefit - inputs.monthlyOperatingCost;
    const paybackMonths = netMonthlyBenefit > 0 ? totalInvestment / netMonthlyBenefit : 999;
    const breakEvenMonth = inputs.implementationTimeMonths + paybackMonths;

    const monthlyDiscountRate = Math.pow(1 + inputs.discountRate / 100, 1 / 12) - 1;
    const calculateNPV = (months: number) => {
      let npv = -totalInvestment;
      for (let month = 1; month <= months; month++) {
        npv += netMonthlyBenefit / Math.pow(1 + monthlyDiscountRate, month);
      }
      return npv;
    };

    const riskAdjustedROI = roi * (inputs.successProbability / 100);
    let recommendation = '';
    if (riskAdjustedROI > 200) recommendation = 'Excellent investment - Strong returns expected';
    else if (riskAdjustedROI > 100) recommendation = 'Good investment - Positive ROI with manageable risk';
    else if (riskAdjustedROI > 50) recommendation = 'Moderate investment - Consider if strategic value is high';
    else if (riskAdjustedROI > 0) recommendation = 'Marginal investment - Evaluate alternatives';
    else recommendation = 'Poor investment - Does not meet ROI threshold';

    const insights: string[] = [];
    if (paybackMonths < 12) insights.push('Quick payback period - less than 1 year');
    else if (paybackMonths < 24) insights.push('Moderate payback period - 1-2 years');
    else if (paybackMonths < 999) insights.push('Long payback period - requires sustained commitment');
    else insights.push('Warning: Benefits do not cover costs');

    if (timeSavingsMonthly > monthlyBenefit * 0.5) insights.push('Primary benefit from time savings - productivity gain');
    if (inputs.revenueIncreaseMonthly > monthlyBenefit * 0.3) insights.push('Significant revenue impact - direct business value');
    if (inputs.successProbability < 70) insights.push('High risk factor - consider mitigation strategies');

    return {
      totalInvestment,
      annualBenefit,
      monthlyBenefit,
      roi,
      paybackMonths,
      npv3Year: calculateNPV(36),
      npv5Year: calculateNPV(60),
      breakEvenMonth,
      riskAdjustedROI,
      recommendation,
      insights,
    };
  }, [inputs]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const scores: Record<string, number> = {
      roi: result.roi,
      riskAdjustedROI: result.riskAdjustedROI,
      paybackMonths: result.paybackMonths,
      npv3Year: result.npv3Year,
      npv5Year: result.npv5Year,
    };

    const investmentPriority: 'high' | 'medium' | 'low' = result.riskAdjustedROI > 100 ? 'high' : result.riskAdjustedROI > 50 ? 'medium' : 'low';
    const recommendations = [
      {
        id: 'investment-recommendation',
        title: result.recommendation.split(' - ')[0],
        priority: investmentPriority,
        description: result.recommendation,
      },
      ...result.insights.map((insight, idx) => ({
        id: `insight-${idx}`,
        title: insight,
        priority: 'medium' as const,
        description: insight,
      })),
    ];

    const assessment = await saveResult({
      name: `Engineering ROI Analysis - ${new Date().toLocaleDateString()}`,
      score_overall: result.roi,
      scores,
      answers: {
        inputs,
      },
      recommendations,
      metadata: {
        roi: result.roi,
        riskAdjustedROI: result.riskAdjustedROI,
        totalInvestment: result.totalInvestment,
        monthlyBenefit: result.monthlyBenefit,
        annualBenefit: result.annualBenefit,
        paybackMonths: result.paybackMonths,
        breakEvenMonth: result.breakEvenMonth,
        npv3Year: result.npv3Year,
        npv5Year: result.npv5Year,
        recommendation: result.recommendation,
        insights: result.insights,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">📈</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Engineering ROI Calculator</h1>
          </div>
          <p className="text-xl text-foreground-secondary">Calculate return on investment for engineering initiatives</p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Engineering ROI Calculator</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Investment Costs</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Initial Investment</label>
                  <input type="number" inputMode="numeric" min="0" step="1000" value={inputs.initialInvestment}
                    onChange={(e) => updateInput('initialInvestment', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Monthly Operating Cost</label>
                  <input type="number" inputMode="numeric" min="0" step="100" value={inputs.monthlyOperatingCost}
                    onChange={(e) => updateInput('monthlyOperatingCost', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Implementation Time (Months)</label>
                  <input type="number" inputMode="numeric" min="1" max="24" value={inputs.implementationTimeMonths}
                    onChange={(e) => updateInput('implementationTimeMonths', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Expected Benefits</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Time Saved (Hours/Week)</label>
                  <input type="number" inputMode="numeric" min="0" step="1" value={inputs.timeSavedHoursPerWeek}
                    onChange={(e) => updateInput('timeSavedHoursPerWeek', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Engineer Hourly Cost ($)</label>
                  <input type="number" inputMode="numeric" min="0" step="5" value={inputs.averageEngineerHourlyCost}
                    onChange={(e) => updateInput('averageEngineerHourlyCost', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Monthly Revenue Increase</label>
                  <input type="number" inputMode="numeric" min="0" step="1000" value={inputs.revenueIncreaseMonthly}
                    onChange={(e) => updateInput('revenueIncreaseMonthly', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Monthly Cost Savings</label>
                  <input type="number" inputMode="numeric" min="0" step="100" value={inputs.costSavingsMonthly}
                    onChange={(e) => updateInput('costSavingsMonthly', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Risk Factors</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Success Probability: {inputs.successProbability}%</label>
                  <input type="range" min="10" max="100" value={inputs.successProbability}
                    onChange={(e) => updateInput('successProbability', parseInt(e.target.value))}
                    className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Discount Rate: {inputs.discountRate}%</label>
                  <input type="range" min="0" max="20" value={inputs.discountRate}
                    onChange={(e) => updateInput('discountRate', parseInt(e.target.value))}
                    className="w-full" />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">ROI Analysis</h3>
              <div className="text-center py-6 mb-4">
                <div className="text-6xl font-bold text-accent mb-2">{result.roi.toFixed(0)}%</div>
                <div className="text-lg text-foreground-secondary">Return on Investment</div>
                <div className="text-sm text-foreground-tertiary mt-2">Risk-Adjusted: {result.riskAdjustedROI.toFixed(0)}%</div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Payback Period</div>
                  <div className="text-2xl font-bold text-accent">{result.paybackMonths < 999 ? result.paybackMonths.toFixed(1) : '∞'}</div>
                  <div className="text-xs text-foreground-tertiary">months</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Break-Even</div>
                  <div className="text-2xl font-bold text-accent">{result.breakEvenMonth < 999 ? result.breakEvenMonth.toFixed(1) : '∞'}</div>
                  <div className="text-xs text-foreground-tertiary">months</div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Total Investment</span>
                  <span className="text-accent font-bold">{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="flex justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Monthly Benefit</span>
                  <span className="text-green-400 font-bold">{formatCurrency(result.monthlyBenefit)}</span>
                </div>
                <div className="flex justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Annual Benefit</span>
                  <span className="text-green-400 font-bold">{formatCurrency(result.annualBenefit)}</span>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Net Present Value</h3>
              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">3-Year NPV</span>
                  <span className={`font-bold ${result.npv3Year >= 0 ? 'text-green-400' : 'text-red-500'}`}>
                    {formatCurrency(result.npv3Year)}
                  </span>
                </div>
                <div className="flex justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">5-Year NPV</span>
                  <span className={`font-bold ${result.npv5Year >= 0 ? 'text-green-400' : 'text-red-500'}`}>
                    {formatCurrency(result.npv5Year)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-accent-muted border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-accent mb-3">Recommendation</h3>
              <p className="text-foreground-secondary mb-4">{result.recommendation}</p>
              <h4 className="font-semibold text-foreground mb-2">Key Insights:</h4>
              <ul className="space-y-2">
                {result.insights.map((insight, i) => (
                  <li key={i} className="text-sm text-foreground-secondary">• {insight}</li>
                ))}
              </ul>
            </div>

            {/* Command Center Save Button */}
            <div className="flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
